"use client";
import {
  LeaderboardProvider,
  useLeaderboard,
} from "@/components/QuestsV2/Points/Leaderboard/LeaderboardProvider";
import type { Campaign } from "@/components/QuestsV2/types";
import { LeaderboardV2 } from "@/components/ui/custom/leaderboard-v2";
import { MediaCarousel } from "@/components/ui/custom/media-carousel";
import { Action } from "@/context/AnalyticsProvider";
import { Swords, Trophy } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import { QuestInteractionHandler } from "./QuestInteractionHandler";
import { getBadgeConfig } from "./badge-registry";
import { FrostedTabs, type TabData } from "./frosted-tabs";
import { MainQuest } from "./main-quest";
import { PageTitle } from "./page-title";
import { QuestCard } from "./quest-card";
import { QuestGroup } from "./quest-group";
import type { GameData } from "./types";
import { useGameDetails } from "./useGameDetails";
import useGameDetailsAnalytics from "./useGameDetailsAnalytics";

interface GameDetailsV2Props {
  gameData: GameData;
}

const Leaderboard = ({
  campaign,
  gameData,
}: {
  campaign: Campaign;
  gameData: GameData;
}) => {
  const { leaderboard, isLoading } = useLeaderboard();
  return (
    <LeaderboardV2
      campaign={campaign}
      gameData={gameData}
      leaderboardData={leaderboard}
      isLoading={isLoading}
      isCreatorLeaderboard={campaign.type === "creator"}
    />
  );
};

export default function GameDetailsV2({ gameData }: GameDetailsV2Props) {
  const {
    totalQuestsCompleted,
    incompleteQuests,
    allQuests,
    completeQuest,
    inProgressQuest,
    setInProgressQuest,
    onQuestClick,
    isWishlisted,
    toggleWishlist,
  } = useGameDetails(gameData);
  const { trackQuestEvent, trackGameEvent } = useGameDetailsAnalytics();
  const router = useRouter();

  const [activeTab, setActiveTab] = useState(
    gameData.campaigns[0]?.startDate < new Date() &&
      gameData.campaigns[0]?.endDate > new Date()
      ? "leaderboard"
      : "quests",
  );
  const nextQuest = incompleteQuests.shift();
  // We don't want to show a leaderboard before it's started
  const selectedCampaign = gameData.campaigns.find(
    (campaign) => campaign.startDate <= new Date(),
  );
  const campaignId = selectedCampaign?.id;

  const tabs: TabData[] = [
    {
      id: "quests",
      icon: <Swords className="w-6 h-6 text-white" />,
      title: "Quests",
      subtitle: `${totalQuestsCompleted} of ${gameData.quests.length} completed`,
      isActive: activeTab === "quests",
    },
    {
      id: "leaderboard",
      icon: <Trophy className="w-6 h-6 text-white" />,
      title: "Leaderboard",
      subtitle: selectedCampaign?.name || "",
      isActive: activeTab === "leaderboard",
    },
  ];

  // If the game doesn't have many splash images limit to 3 next quests
  const nextQuests = incompleteQuests.slice(
    0,
    incompleteQuests.length < 5
      ? incompleteQuests.length
      : gameData.details.splash_urls.length < 5
        ? 3
        : 5,
  );
  const campaignQuests = allQuests.filter((quest) => quest.campaignPoints > 0);

  const handleTwitchDropsClick = useCallback(() => {
    trackGameEvent(Action.PRESSED, "TwitchDrops", gameData.details);
    router.push(`/twitch-drops/${gameData.details.slug}`);
  }, [router, gameData.details, trackGameEvent]);

  return (
    <div className="relative w-full overflow-hidden @container/main">
      {/* Page Title - Full Width */}
      <PageTitle
        gameDetails={gameData.details}
        badges={gameData.badges}
        playUrl={gameData.details.landing_page_url}
        isWishlisted={isWishlisted}
        toggleWishlist={() => {
          trackGameEvent(Action.PRESSED, "Wishlist", gameData.details, {
            isWishlisted: isWishlisted,
          });
          toggleWishlist();
        }}
        handleTwitchDropsClick={handleTwitchDropsClick}
      />

      {/* Content Grid - Starts below Page Title */}
      <div className="relative @6xl/main:grid @6xl/main:grid-cols-[1fr_30rem] @6xl/main:h-full">
        {/* Main Content */}
        <div className="relative @container/leftContent">
          {nextQuest && (
            <div className="px-4 sm:px-6 pt-6 pb-4 sm:pb-6">
              {gameData.details.game_id ===
                "cc6014fb-e976-47b4-99b7-8c5652cbb68f" && (
                <a
                  href="https://www.etherlink.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-inherit no-underline hover:text-blue-400 transition-colors duration-200"
                >
                  <Image
                    src="/images/quests/etherlink.jpg"
                    alt="Etherlink"
                    width={20}
                    height={20}
                    className="rounded-full"
                  />
                  <span>Gaming on Etherlink</span>
                </a>
              )}
              <MainQuest
                quest={
                  // If wishlist quest show the game description as the caption
                  nextQuest.type === "wishlist" &&
                  !!gameData.gameContent?.carousel_description
                    ? {
                        ...nextQuest,
                        caption: gameData.gameContent.carousel_description,
                      }
                    : nextQuest
                }
                onClick={() => {
                  trackQuestEvent(Action.PRESSED, "MainQuest", nextQuest);
                  onQuestClick(nextQuest);
                }}
              />
            </div>
          )}

          {!!selectedCampaign && (
            <FrostedTabs
              className="px-4 sm:px-6 pt-6"
              tabs={tabs}
              onTabChange={(tabId) => {
                setActiveTab(tabId);
                trackGameEvent(Action.PRESSED, "Tab", gameData.details, {
                  tab: tabId,
                });
              }}
            />
          )}

          <div className="@container space-y-4 backdrop-blur-[20px]">
            <div className="flex-1 h-px bg-foreground/15" />

            {activeTab === "quests" && (
              <>
                <h2 className="text-foreground text-xl font-semibold leading-7 px-6">
                  Next quests
                </h2>

                <div className="w-full flex gap-5 overflow-x-auto @3xl:grid @3xl:grid-cols-6 @3xl:gap-5 @3xl:overflow-visible scroll-smooth [&::-webkit-scrollbar]:hidden  @6xl/main:pr-6 px-6">
                  {nextQuests.map((quest, index) => (
                    <QuestCard
                      key={quest.externalId}
                      quest={quest}
                      disabled={!isWishlisted && quest.type !== "wishlist"}
                      backgroundImage={
                        gameData.details.splash_urls[
                          index % gameData.details.splash_urls.length
                        ]
                      }
                      badge={getBadgeConfig(quest.type)}
                      className={`shrink-0 w-64 h-auto @3xl:w-auto ${nextQuests.length % 2 === 0 || (nextQuests.length === 5 && (index === 0 || index === 1)) ? "@3xl:col-span-3" : "col-span-2"}`}
                      onClick={() => {
                        trackQuestEvent(Action.PRESSED, "NextQuests", quest, {
                          cardIndex: index,
                        });
                        onQuestClick(quest);
                      }}
                    />
                  ))}
                </div>
                <div className="flex-1 h-px bg-foreground/15" />

                {/* All Quests - only show in main content on smaller containers */}
                <div className="@6xl/main:hidden px-4">
                  <QuestGroup
                    quests={allQuests}
                    name="All Quests"
                    isWishlisted={isWishlisted}
                    onClick={(quest) => {
                      trackQuestEvent(Action.PRESSED, "AllQuests", quest);
                      onQuestClick(quest);
                    }}
                  />
                </div>
              </>
            )}

            {activeTab === "leaderboard" && !!selectedCampaign && (
              <div className="px-4 py-6">
                {campaignId ? (
                  <div
                    className={`grid grid-cols-1 gap-6 ${selectedCampaign?.type === "standard" ? "@5xl/leftContent:grid-cols-[1fr_30rem]" : ""}`}
                  >
                    <LeaderboardProvider
                      campaignId={campaignId}
                      isCreatorLeaderboard={
                        selectedCampaign?.type === "creator"
                      }
                    >
                      <Leaderboard
                        campaign={selectedCampaign}
                        gameData={gameData}
                      />
                    </LeaderboardProvider>

                    {campaignQuests.length > 0 &&
                      selectedCampaign?.type === "standard" && (
                        <QuestGroup
                          quests={campaignQuests}
                          name={"Leaderboard Quests"}
                          isWishlisted={isWishlisted}
                          showProgress={false}
                          onClick={(quest) => {
                            trackQuestEvent(
                              Action.PRESSED,
                              "CampaignQuests",
                              quest,
                            );
                            onQuestClick(quest);
                          }}
                        />
                      )}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground p-6">
                    No active campaign available for leaderboard
                  </div>
                )}
              </div>
            )}

            {/* Media Carousel */}
            {gameData.details.carousel_urls &&
              gameData.details.carousel_urls.length > 0 && (
                <div className="pt-2 px-6">
                  <MediaCarousel
                    urls={gameData.details.carousel_urls}
                    onSlideChange={(url) => {
                      trackGameEvent(
                        Action.VIEWED,
                        "MediaCarousel",
                        gameData.details,
                        {
                          mediaUrl: url,
                        },
                      );
                    }}
                  />
                </div>
              )}
          </div>
        </div>

        {/* All Quests Sidebar - only show on large containers */}
        <div className="hidden @6xl/main:block h-full backdrop-blur-[20px] border-l border-foreground/15 px-6 pt-6">
          <QuestGroup
            quests={allQuests}
            name="All Quests"
            isWishlisted={isWishlisted}
            onClick={(quest) => {
              trackQuestEvent(Action.PRESSED, "AllQuests", quest);
              onQuestClick(quest);
            }}
          />
        </div>
      </div>

      <span className="text-sm text-foreground/60 p-2">
        Unsubscribe at any time by following the instructions in our emails
      </span>

      <QuestInteractionHandler
        game={gameData}
        completeQuest={completeQuest}
        inProgressQuest={inProgressQuest}
        setInProgressQuest={setInProgressQuest}
      />
    </div>
  );
}
